import ReactMarkdown from 'react-markdown';

import { Empty } from '@/components/UI/Empty';
import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { useLocale } from '@/renderer/utils/locale';
import { useData } from '@/renderer/utils/useData';

import type { PostData } from './dataFetcher';

const BASE_URL = 'https://www.raspicks.com';

export default WallsPostPage;

// Per-post <title>/description/og come from +title.ts and +description.ts.
function WallsPostPage() {
  const { post } = useData<PostData>();
  const { formatDate } = useLocale();

  if (!post) {
    return (
      <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
        <Header title="Walls' Blog" className="py-2 md:hidden" />
        <div className="overflow-auto">
          <div className="mx-auto max-w-3xl px-4 py-20 sm:px-6 lg:px-8">
            <Empty title="Post not found" />
            <p className="mt-4 text-center">
              <a href="/walls" className="font-semibold text-emerald-600 dark:text-emerald-400">
                ← Back to Walls' Blog
              </a>
            </p>
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt || undefined,
    image: post.coverImage || undefined,
    datePublished: post.publishedAt || undefined,
    dateModified: post.publishedAt || undefined,
    author: { '@type': 'Person', name: post.author },
    publisher: {
      '@type': 'Organization',
      name: 'Right Angle Sports',
      logo: { '@type': 'ImageObject', url: `${BASE_URL}/ras-emerald-96.svg` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${BASE_URL}/walls/${post.slug}` },
    url: `${BASE_URL}/walls/${post.slug}`,
  };

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      {/* pageTitle drives <title> + og:title (the app's title mechanism). */}
      <Header title="Walls' Blog" pageTitle={post.title} className="py-2 md:hidden" />
      <div className="overflow-auto">
        <article className="px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
          <div className="mx-auto max-w-2xl">
            <a
              href="/walls"
              className="inline-flex items-center gap-1 font-medium text-emerald-600 text-sm hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300"
            >
              ← Walls' Blog
            </a>

            <header className="mt-6 border-zinc-950/10 border-b pb-6 dark:border-white/10">
              {post.publishedAt && (
                <time className="font-medium text-emerald-600 text-sm dark:text-emerald-400">
                  {formatDate(post.publishedAt, { year: 'numeric', month: 'long', day: 'numeric' })}
                </time>
              )}
              <h1 className="mt-2 font-bold text-3xl text-zinc-900 tracking-tight sm:text-4xl dark:text-zinc-100">
                {post.title}
              </h1>
              <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">By {post.author}</p>
            </header>

            {post.coverImage && (
              <img
                src={post.coverImage}
                alt=""
                className="mt-8 aspect-[16/9] w-full rounded-xl object-cover"
              />
            )}

            <div className="prose prose-sm prose-zinc mt-8 max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:tracking-tight prose-a:font-semibold prose-a:text-emerald-600 prose-a:no-underline hover:prose-a:underline dark:prose-a:text-emerald-400">
              <ReactMarkdown>{post.body}</ReactMarkdown>
            </div>
          </div>
        </article>

        <Footer />
      </div>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
    </div>
  );
}
